import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ModalFrame } from '@/components/ModalFrame';
import { AlertTriangle } from 'lucide-react';
import { useBlocker } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useTimeout } from '@/hooks/useTimeout';
import { useT } from '@/i18n/LocaleProvider';
import { common } from '@featit/shared/i18n/messages/en/common';
import { registerEnglishArea } from '@/i18n/catalogue';

registerEnglishArea('common', common);

interface ChangeSetRegistration {
  id: string;
  label: string;
  currentSignature: string;
  baselineSignature: string;
  ownsSearchParam?: (key: string) => boolean;
}

interface UnsavedChangesContextValue {
  register: (registration: ChangeSetRegistration) => void;
  unregister: (id: string) => void;
  runAfterDiscard: (action: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export interface SettingsChangeSetOptions {
  label: string;
  changeSet: unknown;
  baseline: unknown;
  enabled?: boolean;
  /**
   * Query parameters this page publishes about itself.
   *
   * A page that mirrors its own state into the address navigates while the reader stands still —
   * the custom view editor does it on every filter and drill. Without this the guard read its own
   * mirror as an attempt to leave and asked the author to discard the work they were doing.
   */
  ownsSearchParam?: (key: string) => boolean;
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const registrationsRef = useRef(new Map<string, ChangeSetRegistration>());
  const bypassNextNavigationRef = useRef(false);
  const [dirtyRegistrations, setDirtyRegistrations] = useState<ChangeSetRegistration[]>([]);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const hasUnsavedChanges = dirtyRegistrations.length > 0;

  const dirtyRegistrationsNow = useCallback(
    () =>
      Array.from(registrationsRef.current.values()).filter(
        (registration) => registration.currentSignature !== registration.baselineSignature,
      ),
    [],
  );

  const refreshDirtyRegistrations = useCallback(() => {
    setDirtyRegistrations(dirtyRegistrationsNow());
  }, [dirtyRegistrationsNow]);

  const register = useCallback(
    (registration: ChangeSetRegistration) => {
      registrationsRef.current.set(registration.id, registration);
      refreshDirtyRegistrations();
    },
    [refreshDirtyRegistrations],
  );

  const unregister = useCallback(
    (id: string) => {
      registrationsRef.current.delete(id);
      refreshDirtyRegistrations();
    },
    [refreshDirtyRegistrations],
  );

  const leavesTheDirtyPage = useCallback(
    (
      current: { pathname: string; search: string; hash: string },
      next: typeof current,
      dirty: ChangeSetRegistration[],
    ): boolean => {
      if (current.pathname !== next.pathname || current.hash !== next.hash) return true;
      if (current.search === next.search) return false;
      // Same page, different query. Whether that is "leaving" depends on who wrote the parameter:
      // a page republishing its own state has not gone anywhere, so only a parameter no dirty
      // registration claims counts.
      const owns = dirty
        .map((registration) => registration.ownsSearchParam)
        .filter((predicate): predicate is (key: string) => boolean => Boolean(predicate));
      if (owns.length !== dirty.length) return true;
      const currentParams = new URLSearchParams(current.search);
      const nextParams = new URLSearchParams(next.search);
      const keys = new Set([...currentParams.keys(), ...nextParams.keys()]);
      for (const key of keys) {
        if (currentParams.get(key) === nextParams.get(key)) continue;
        if (!owns.some((predicate) => predicate(key))) return true;
      }
      return false;
    },
    [],
  );

  /*
   * ⛔ The registrations as they are now, never `hasUnsavedChanges` as last rendered. The router keeps
   * the function it was handed in its own passive effect, and the provider renders a change only after
   * the edit has committed: a leave that arrived in between — a click the browser delivers before
   * React's next task, the drop at the end of a drag — was judged clean and went through, and the
   * draft with it, no dialog. Stable, so the router always holds this one.
   */
  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string; search: string; hash: string };
      nextLocation: { pathname: string; search: string; hash: string };
    }) => {
      if (bypassNextNavigationRef.current) return false;
      const dirty = dirtyRegistrationsNow();
      return dirty.length > 0 && leavesTheDirtyPage(currentLocation, nextLocation, dirty);
    },
    [dirtyRegistrationsNow, leavesTheDirtyPage],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  const runAfterDiscard = useCallback(
    (action: () => void) => {
      if (!hasUnsavedChanges) {
        action();
        return;
      }
      setPendingAction(() => action);
    },
    [hasUnsavedChanges],
  );

  const contextValue = useMemo(
    () => ({ register, unregister, runAfterDiscard }),
    [register, runAfterDiscard, unregister],
  );
  // Owned, so a provider that unmounts before it fires leaves nothing armed behind it (I-0360).
  const bypassReset = useTimeout();
  /*
   * ⛔ I-0820 (QA 270): a save that leaves the page in the same tick — the view editor saves, turns
   * editing off and navigates — was judged by the render before the save, so the navigation was
   * blocked, and by the time the dialog drew nothing was dirty: "unsaved changes in 0 settings
   * sections" after a save. A blocked navigation with nothing left dirty goes through, and the
   * dialog is only ever drawn over something it can name.
   */
  const blockedWithNothingDirty = blocker.state === 'blocked' && !hasUnsavedChanges;
  useEffect(() => {
    if (blockedWithNothingDirty) blocker.proceed?.();
  }, [blocker, blockedWithNothingDirty]);
  useEffect(() => {
    if (pendingAction === null || hasUnsavedChanges) return;
    setPendingAction(null);
    pendingAction();
  }, [hasUnsavedChanges, pendingAction]);
  const dialogOpen =
    (blocker.state === 'blocked' && hasUnsavedChanges) ||
    (pendingAction !== null && hasUnsavedChanges);

  function keepEditing(): void {
    setPendingAction(null);
    if (blocker.state === 'blocked') blocker.reset();
  }

  function discardAndContinue(): void {
    const action = pendingAction;
    setPendingAction(null);
    if (blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }
    if (!action) return;

    bypassNextNavigationRef.current = true;
    action();
    bypassReset.schedule(() => {
      bypassNextNavigationRef.current = false;
    }, 0);
  }

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
      {dialogOpen ? (
        <ModalFrame
          role="alertdialog"
          onClose={keepEditing}
          labelledBy="unsaved-changes-title"
          describedBy="unsaved-changes-description"
          closeOnBackdrop={false}
          layerClassName="z-[100] grid place-items-center p-4"
          backdropClassName="bg-black/45"
          panelClassName="relative w-full max-w-md"
        >
          <section className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl dark:bg-[#212121]">
            <div className="flex items-start gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                <AlertTriangle className="h-4.5 w-4.5" />
              </span>
              <div className="min-w-0">
                <h2
                  id="unsaved-changes-title"
                  className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('common.unsavedChanges.title')}
                </h2>
                <p
                  id="unsaved-changes-description"
                  className="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-400"
                >
                  {dirtyRegistrations.length === 1
                    ? t('common.unsavedChanges.inOne', {
                        section: dirtyRegistrations[0].label,
                      })
                    : t('common.unsavedChanges.inMany', {
                        count: dirtyRegistrations.length,
                      })}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={keepEditing} autoFocus>
                {t('common.unsavedChanges.keep')}
              </Button>
              <Button variant="danger" onClick={discardAndContinue}>
                {t('common.unsavedChanges.discard')}
              </Button>
            </div>
          </section>
        </ModalFrame>
      ) : null}
    </UnsavedChangesContext.Provider>
  );
}

export function useSettingsChangeSet({
  label,
  changeSet,
  baseline,
  enabled = true,
  ownsSearchParam,
}: SettingsChangeSetOptions): { isDirty: boolean } {
  const context = useContext(UnsavedChangesContext);
  const registrationId = useId();
  const currentSignature = useMemo(() => stableSerialize(changeSet), [changeSet]);
  const baselineSignature = useMemo(() => stableSerialize(baseline), [baseline]);
  const isDirty = enabled && currentSignature !== baselineSignature;

  // A layout effect: the registration lands in the commit that made the change, so a leave that
  // follows it before any passive effect has run (a drag's drop is not a discrete event, and its
  // passive effects wait for a task) is still judged against it.
  useLayoutEffect(() => {
    if (!context) return;
    if (!enabled) {
      context.unregister(registrationId);
      return;
    }
    context.register({
      id: registrationId,
      label,
      currentSignature,
      baselineSignature,
      ownsSearchParam,
    });
    return () => context.unregister(registrationId);
  }, [
    baselineSignature,
    context,
    currentSignature,
    enabled,
    label,
    ownsSearchParam,
    registrationId,
  ]);

  return { isDirty };
}

export function useUnsavedChangesActions(): Pick<UnsavedChangesContextValue, 'runAfterDiscard'> {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChangesActions must be used inside UnsavedChangesProvider.');
  }
  return { runAfterDiscard: context.runAfterDiscard };
}

export function stableSerialize(value: unknown): string {
  const ancestors = new Set<object>();

  function visit(current: unknown): unknown {
    if (current === undefined) return ['undefined'];
    if (current === null || typeof current === 'string' || typeof current === 'boolean') {
      return [typeof current, current];
    }
    if (typeof current === 'number') {
      if (Number.isNaN(current)) return ['number', 'NaN'];
      if (!Number.isFinite(current)) return ['number', String(current)];
      return ['number', current];
    }
    if (typeof current === 'bigint') return ['bigint', current.toString()];
    if (current instanceof Date) return ['date', current.toISOString()];
    if (typeof current !== 'object') {
      throw new TypeError(`Settings change sets cannot contain ${typeof current} values.`);
    }
    if (ancestors.has(current)) {
      throw new TypeError('Settings change sets must not contain circular references.');
    }

    ancestors.add(current);
    const normalized = Array.isArray(current)
      ? ['array', current.map(visit)]
      : [
          'object',
          Object.keys(current)
            .sort()
            .map((key) => [key, visit((current as Record<string, unknown>)[key])]),
        ];
    ancestors.delete(current);
    return normalized;
  }

  return JSON.stringify(visit(value));
}
